import { create } from "zustand";
import { fetchWebsites, fetchWebsiteByBaseUrl, createWebsite } from "./websitesApi";
import { fetchGscStatus, fetchGscSites } from "./googleSearchConsoleApi";
import { notifications } from "@mantine/notifications";
import i18n from "../i18n";

export const useWebsitesStore = create((set, get) => ({
  websites: null,
  isLoading: true,
  selectedWebsite: null,
  
  // Google Search Console state
  gscConnected: false,
  gscSites: null,
  gscLoading: false,

  fetchWebsites: async () => {
    set({ isLoading: true });
    try {
      const websites = await fetchWebsites();
      set({
        websites,
        isLoading: false,
      });
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "websites.notifications.fetch_failed",
          "Failed to fetch websites"
        ),
        color: "red",
      });
      set({ isLoading: false });
    }
  },

  fetchWebsiteByBaseUrl: async (baseUrl) => {
    set({ isLoading: true });
    try {
      const website = await fetchWebsiteByBaseUrl(baseUrl);
      set({
        selectedWebsite: website,
        isLoading: false,
      });
      return website;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "websites.notifications.fetch_single_failed",
          "Failed to fetch website details"
        ),
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },

  createWebsite: async (baseUrl) => {
    set({ isLoading: true });
    try {
      const newWebsite = await createWebsite(baseUrl);
      set((state) => ({
        websites: state.websites ? [...state.websites, newWebsite] : [newWebsite],
        isLoading: false,
      }));
      notifications.show({
        title: i18n.t(
          "websites.notifications.create_success",
          "Website added successfully"
        ),
        color: "green",
      });
      return newWebsite;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "websites.notifications.create_failed",
          "Failed to add website"
        ),
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },

  getWebsiteById: (websiteId) => {
    const existingWebsite = get().websites?.find(
      (w) => w.id === websiteId
    );
    if (existingWebsite) {
      return existingWebsite;
    }
    return null;
  },

  clearSelectedWebsite: () => {
    set({ selectedWebsite: null });
  },

  // Google Search Console actions
  checkGscConnection: async () => {
    try {
      const result = await fetchGscStatus();
      set({ gscConnected: result.isConnected });
      return result.isConnected;
    } catch (err) {
      set({ gscConnected: false });
      return false;
    }
  },

  fetchGscSites: async () => {
    set({ gscLoading: true });
    try {
      const result = await fetchGscSites();
      set({
        gscSites: result.sites,
        gscConnected: true,
        gscLoading: false,
      });
      notifications.show({
        title: i18n.t(
          "websites.notifications.gsc_fetch_success",
          "Google Search Console sites fetched"
        ),
        message: i18n.t(
          "websites.notifications.gsc_fetch_success_message",
          `Found ${result.count} verified site(s)`
        ),
        color: "green",
      });
      return result.sites;
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      notifications.show({
        title: i18n.t(
          "websites.notifications.gsc_fetch_failed",
          "Failed to fetch Google Search Console sites"
        ),
        message: errorMessage,
        color: "red",
      });
      set({ gscLoading: false, gscConnected: false });
      return null;
    }
  },

  importGscSite: async (siteUrl) => {
    set({ isLoading: true });
    try {
      // Remove protocol and trailing slash to get clean base URL
      const cleanUrl = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const newWebsite = await createWebsite(`https://${cleanUrl}`);
      
      set((state) => ({
        websites: state.websites ? [...state.websites, newWebsite] : [newWebsite],
        isLoading: false,
      }));
      
      notifications.show({
        title: i18n.t(
          "websites.notifications.import_success",
          "Site imported successfully"
        ),
        message: i18n.t(
          "websites.notifications.import_success_message",
          `${siteUrl} has been added to your crawled websites`
        ),
        color: "green",
      });
      return newWebsite;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "websites.notifications.import_failed",
          "Failed to import site"
        ),
        message: err.response?.data?.error || err.message,
        color: "red",
      });
      set({ isLoading: false });
      return null;
    }
  },
}));
